import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { getTablePreferences, reorder, move, setTablePreferences } from 'components/utils';
import { DragReorderRow, toggleReorderPopoverAnchor } from 'components/common/utils';

/** Two-slot layout (candidate / client). */
export type TwoColumnState<T> = { left: T[]; right: T[] };

/** Four-slot layout (job detail): top row, left/right middle, bottom row. */
export type FourCornerCardState<T> = { top: T[]; left: T[]; right: T[]; bottom: T[] };

export type CardLayoutReorderHandle = {
  toggleCardsReorderPopover: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export type QuadSlotKey = 'top' | 'left' | 'right' | 'bottom';

const QUAD_KEYS: QuadSlotKey[] = ['top', 'left', 'right', 'bottom'];

/** Drop ids not present in defaults and duplicate entries (bad prefs / manual edits). */
function filterToKnownSectionOrder<T>(order: T[], validIds: Set<T>): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const id of order) {
    if (validIds.has(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Merge saved two-slot state with defaults so new section ids appear when the product adds cards */
export function mergeTwoColumnOrderWithDefaults<T>(
  saved: TwoColumnState<T> | undefined,
  defaults: TwoColumnState<T>,
): TwoColumnState<T> {
  if (!saved || !Array.isArray(saved.left) || !Array.isArray(saved.right)) {
    return defaults;
  }

  const validIds = new Set([...defaults.left, ...defaults.right]);
  const cleaned: TwoColumnState<T> = {
    left: filterToKnownSectionOrder(saved.left, validIds),
    right: filterToKnownSectionOrder(saved.right, validIds),
  };

  const allDefaultIds = new Set([...defaults.left, ...defaults.right]);
  const allSavedIds = new Set([...cleaned.left, ...cleaned.right]);

  const missing: T[] = [];
  allDefaultIds.forEach(id => {
    if (!allSavedIds.has(id)) {
      missing.push(id);
    }
  });

  if (missing.length === 0) {
    return cleaned;
  }

  const result: TwoColumnState<T> = {
    left: [...cleaned.left],
    right: [...cleaned.right],
  };

  missing.forEach(id => {
    if (defaults.left.some(x => x === id)) {
      result.left.push(id);
    } else {
      result.right.push(id);
    }
  });

  return result;
}

export function mergeFourCornerOrderWithDefaults<T>(
  saved: FourCornerCardState<T> | undefined,
  defaults: FourCornerCardState<T>,
): FourCornerCardState<T> {
  if (!saved || !QUAD_KEYS.every(k => Array.isArray(saved[k]))) {
    return defaults;
  }

  const raw = saved;
  const validIds = new Set(QUAD_KEYS.flatMap(k => defaults[k]));
  const cleaned: FourCornerCardState<T> = {
    top: filterToKnownSectionOrder(raw.top, validIds),
    left: filterToKnownSectionOrder(raw.left, validIds),
    right: filterToKnownSectionOrder(raw.right, validIds),
    bottom: filterToKnownSectionOrder(raw.bottom, validIds),
  };

  const allDefaultIds = new Set(QUAD_KEYS.flatMap(k => defaults[k]));
  const allSavedIds = new Set(QUAD_KEYS.flatMap(k => cleaned[k]));

  const missing: T[] = [];
  allDefaultIds.forEach(id => {
    if (!allSavedIds.has(id)) {
      missing.push(id);
    }
  });

  if (missing.length === 0) {
    return cleaned;
  }

  const result: FourCornerCardState<T> = {
    top: [...cleaned.top],
    left: [...cleaned.left],
    right: [...cleaned.right],
    bottom: [...cleaned.bottom],
  };

  missing.forEach(id => {
    const slot = QUAD_KEYS.find(k => defaults[k].some(x => x === id));
    if (slot) {
      result[slot].push(id);
    }
  });

  return result;
}

export function applyTwoColumnDragResult<T>(
  result: DropResult,
  columns: TwoColumnState<T>,
  firstDroppableId: string,
  secondDroppableId: string,
): TwoColumnState<T> | null {
  const { source, destination } = result;

  if (!destination) {
    return null;
  }

  if (source.droppableId === destination.droppableId && source.index === destination.index) {
    return null;
  }

  const { left: firstCol, right: secondCol } = columns;
  const sourceList = source.droppableId === firstDroppableId ? firstCol : secondCol;

  if (source.droppableId === destination.droppableId) {
    const items = reorder(sourceList, source.index, destination.index);
    return {
      left: source.droppableId === firstDroppableId ? items : firstCol,
      right: source.droppableId === secondDroppableId ? items : secondCol,
    };
  }

  const destList = destination.droppableId === firstDroppableId ? firstCol : secondCol;
  const moved = move(sourceList, destList, source.index, destination.index);
  if (source.droppableId === firstDroppableId) {
    return { left: moved.source, right: moved.destination };
  }

  return { left: moved.destination, right: moved.source };
}

function quadSlotFromDroppableId(droppableId: string, ids: Record<QuadSlotKey, string>): QuadSlotKey | null {
  for (const k of QUAD_KEYS) {
    if (ids[k] === droppableId) {
      return k;
    }
  }
  return null;
}

export function applyFourCornerDragResult<T>(
  result: DropResult,
  columns: FourCornerCardState<T>,
  droppableIds: Record<QuadSlotKey, string>,
): FourCornerCardState<T> | null {
  const { source, destination } = result;
  if (!destination) {
    return null;
  }

  const srcKey = quadSlotFromDroppableId(source.droppableId, droppableIds);
  const dstKey = quadSlotFromDroppableId(destination.droppableId, droppableIds);
  if (!srcKey || !dstKey) {
    return null;
  }

  if (srcKey === dstKey && source.index === destination.index) {
    return null;
  }

  const next: FourCornerCardState<T> = {
    top: [...columns.top],
    left: [...columns.left],
    right: [...columns.right],
    bottom: [...columns.bottom],
  };

  const sourceList = [...next[srcKey]];

  if (srcKey === dstKey) {
    next[srcKey] = reorder(sourceList, source.index, destination.index);
    return next;
  }

  const destList = [...next[dstKey]];
  const moved = move(sourceList, destList, source.index, destination.index);
  next[srcKey] = moved.source;
  next[dstKey] = moved.destination;
  return next;
}

function getReorderBoardOuterSx(stackDirection: 'horizontal' | 'vertical'): SxProps<Theme> {
  if (stackDirection === 'vertical') {
    return {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      minWidth: 280,
      width: '100%',
      alignItems: 'stretch',
    };
  }

  return {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1,
    minWidth: { xs: 260, sm: 400 },
    alignItems: 'flex-start',
  };
}

type CommonCardLayoutParams<T> = {
  ref: React.Ref<CardLayoutReorderHandle | null>;
  layout: 'tabs' | 'cards';
  preferencesTableName: string;
  preferencesKey?: string;
  getDraggableId: (item: T) => string;
  renderLabel: (item: T) => React.ReactNode;
  droppableType?: string;
};

export type UseCardLayoutReorderDualParams<T> = CommonCardLayoutParams<T> & {
  slotMode?: 'dual';
  defaultColumns: TwoColumnState<T>;
  firstDroppableId?: string;
  secondDroppableId?: string;
  firstSlotLabel?: string;
  secondSlotLabel?: string;
  stackDirection?: 'horizontal' | 'vertical';
  normalizeSaved?: (raw: unknown) => TwoColumnState<T> | undefined;
};

export type UseCardLayoutReorderQuadParams<T> = CommonCardLayoutParams<T> & {
  slotMode: 'four-corner';
  defaultQuadSlots: FourCornerCardState<T>;
  quadDroppableIds: Record<QuadSlotKey, string>;
  quadSlotLabels?: Partial<Record<QuadSlotKey, string>>;
  normalizeSaved?: (raw: unknown) => FourCornerCardState<T> | undefined;
};

export type UseCardLayoutReorderParams<T> = UseCardLayoutReorderDualParams<T> | UseCardLayoutReorderQuadParams<T>;

export type UseCardLayoutReorderDualReturn<T> = {
  slotMode: 'dual';
  cardColumns: TwoColumnState<T>;
  quadSlots: undefined;
  persistCardColumns: (cols: TwoColumnState<T>) => void;
  cardsReorderAnchorEl: HTMLButtonElement | null;
  closeCardsReorderPopover: () => void;
  renderCardsReorderBoard: () => React.ReactElement;
  stackDirection: 'horizontal' | 'vertical';
};

export type UseCardLayoutReorderQuadReturn<T> = {
  slotMode: 'four-corner';
  cardColumns: undefined;
  quadSlots: FourCornerCardState<T>;
  persistQuadSlots: (cols: FourCornerCardState<T>) => void;
  cardsReorderAnchorEl: HTMLButtonElement | null;
  closeCardsReorderPopover: () => void;
  renderCardsReorderBoard: () => React.ReactElement;
};

export function useCardLayoutReorder<T>(params: UseCardLayoutReorderDualParams<T>): UseCardLayoutReorderDualReturn<T>;
export function useCardLayoutReorder<T>(params: UseCardLayoutReorderQuadParams<T>): UseCardLayoutReorderQuadReturn<T>;
export function useCardLayoutReorder<T>(
  params: UseCardLayoutReorderParams<T>,
): UseCardLayoutReorderDualReturn<T> | UseCardLayoutReorderQuadReturn<T> {
  const isQuad = params.slotMode === 'four-corner';

  const {
    ref,
    layout,
    preferencesTableName,
    preferencesKey = 'cardColumns',
    getDraggableId,
    renderLabel,
    droppableType = 'card-layout-item',
  } = params;

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const [cardColumns, setCardColumns] = useState<TwoColumnState<T>>(
    !isQuad ? (params as UseCardLayoutReorderDualParams<T>).defaultColumns : { left: [], right: [] },
  );
  const [quadSlots, setQuadSlots] = useState<FourCornerCardState<T>>(
    isQuad
      ? (params as UseCardLayoutReorderQuadParams<T>).defaultQuadSlots
      : { top: [], left: [], right: [], bottom: [] },
  );
  const [cardsReorderAnchorEl, setCardsReorderAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (layout !== 'cards') {
      return;
    }

    const preferences = getTablePreferences(preferencesTableName);
    const raw = preferences[preferencesKey];
    const p = paramsRef.current;

    if (p.slotMode === 'four-corner') {
      const q = p as UseCardLayoutReorderQuadParams<T>;
      const normalized = q.normalizeSaved ? q.normalizeSaved(raw) : undefined;
      const base = normalized !== undefined ? normalized : (raw as FourCornerCardState<T> | undefined);
      setQuadSlots(mergeFourCornerOrderWithDefaults(base, q.defaultQuadSlots));
    } else {
      const d = p as UseCardLayoutReorderDualParams<T>;
      const normalized = d.normalizeSaved ? d.normalizeSaved(raw) : undefined;
      const base = normalized !== undefined ? normalized : (raw as TwoColumnState<T> | undefined);
      setCardColumns(mergeTwoColumnOrderWithDefaults(base, d.defaultColumns));
    }
  }, [layout, preferencesTableName, preferencesKey, isQuad]);

  useEffect(() => {
    if (layout !== 'cards') {
      setCardsReorderAnchorEl(null);
    }
  }, [layout]);

  useImperativeHandle(
    ref,
    () => ({
      toggleCardsReorderPopover: (e: React.MouseEvent<HTMLButtonElement>) => {
        if (layout !== 'cards') {
          return;
        }
        toggleReorderPopoverAnchor(setCardsReorderAnchorEl, e);
      },
    }),
    [layout],
  );

  const persistCardColumns = useCallback(
    (cols: TwoColumnState<T>) => {
      setCardColumns(cols);
      setTablePreferences({
        tableName: preferencesTableName,
        values: [{ key: preferencesKey, value: cols }],
      });
    },
    [preferencesTableName, preferencesKey],
  );

  const persistQuadSlots = useCallback(
    (cols: FourCornerCardState<T>) => {
      setQuadSlots(cols);
      setTablePreferences({
        tableName: preferencesTableName,
        values: [{ key: preferencesKey, value: cols }],
      });
    },
    [preferencesTableName, preferencesKey],
  );

  const closeCardsReorderPopover = useCallback(() => {
    setCardsReorderAnchorEl(null);
  }, []);

  const renderCardsReorderBoard = useCallback(() => {
    const p = paramsRef.current;

    if (p.slotMode === 'four-corner') {
      const q = p as UseCardLayoutReorderQuadParams<T>;
      const ids = q.quadDroppableIds;
      const labels: Record<QuadSlotKey, string> = {
        top: q.quadSlotLabels?.top ?? 'Top',
        left: q.quadSlotLabels?.left ?? 'Left',
        right: q.quadSlotLabels?.right ?? 'Right',
        bottom: q.quadSlotLabels?.bottom ?? 'Bottom',
      };

      const handleDragEnd = (result: DropResult) => {
        const next = applyFourCornerDragResult(result, quadSlots, ids);
        if (next) {
          persistQuadSlots(next);
        }
      };

      const renderSlotColumn = (slot: QuadSlotKey) => {
        const droppableId = ids[slot];
        const items = quadSlots[slot];
        const title = labels[slot];

        return (
          <Box key={droppableId} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, px: 0.5 }}>
              {title}
            </Typography>
            <Droppable droppableId={droppableId} type={droppableType}>
              {provided => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minHeight: 40,
                    borderRadius: 1,
                  }}
                >
                  {items.map((item, index) => {
                    const draggableId = getDraggableId(item);
                    return (
                      <Draggable key={draggableId} draggableId={draggableId} index={index}>
                        {(draggableProvided, snapshot) => (
                          <DragReorderRow
                            draggableProvided={draggableProvided}
                            snapshot={snapshot}
                            label={renderLabel(item)}
                          />
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Box>
        );
      };

      return (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 280,
              width: '100%',
              alignItems: 'stretch',
            }}
          >
            {renderSlotColumn('top')}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: '100%' }}>
              {renderSlotColumn('left')}
              {renderSlotColumn('right')}
            </Box>
            {renderSlotColumn('bottom')}
          </Box>
        </DragDropContext>
      );
    }

    const d = p as UseCardLayoutReorderDualParams<T>;
    const firstDroppableId = d.firstDroppableId ?? 'card-layout-slot-a';
    const secondDroppableId = d.secondDroppableId ?? 'card-layout-slot-b';
    const firstSlotLabel = d.firstSlotLabel ?? 'Left column';
    const secondSlotLabel = d.secondSlotLabel ?? 'Right column';
    const stackDirection = d.stackDirection ?? 'horizontal';

    const handleDragEnd = (result: DropResult) => {
      const next = applyTwoColumnDragResult(result, cardColumns, firstDroppableId, secondDroppableId);
      if (next) {
        persistCardColumns(next);
      }
    };

    const columnDefs: {
      droppableId: string;
      columnKey: 'left' | 'right';
      title: string;
    }[] = [
      { droppableId: firstDroppableId, columnKey: 'left', title: firstSlotLabel },
      { droppableId: secondDroppableId, columnKey: 'right', title: secondSlotLabel },
    ];

    const slotBoxSx =
      stackDirection === 'vertical'
        ? { flex: 'none', minWidth: 0, width: '100%' }
        : { flex: 1, minWidth: 0, width: '100%' };

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={getReorderBoardOuterSx(stackDirection)}>
          {columnDefs.map(({ droppableId, columnKey, title }) => {
            const items = cardColumns[columnKey];

            return (
              <Box key={droppableId} sx={slotBoxSx}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, px: 0.5 }}>
                  {title}
                </Typography>
                <Droppable droppableId={droppableId} type={droppableType}>
                  {provided => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: 40,
                        borderRadius: 1,
                      }}
                    >
                      {items.map((item, index) => {
                        const draggableId = getDraggableId(item);

                        return (
                          <Draggable key={draggableId} draggableId={draggableId} index={index}>
                            {(draggableProvided, snapshot) => (
                              <DragReorderRow
                                draggableProvided={draggableProvided}
                                snapshot={snapshot}
                                label={renderLabel(item)}
                              />
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
            );
          })}
        </Box>
      </DragDropContext>
    );
  }, [
    isQuad,
    cardColumns,
    quadSlots,
    persistCardColumns,
    persistQuadSlots,
    droppableType,
    getDraggableId,
    renderLabel,
  ]);

  if (isQuad) {
    return {
      slotMode: 'four-corner',
      cardColumns: undefined,
      quadSlots,
      persistQuadSlots,
      cardsReorderAnchorEl,
      closeCardsReorderPopover,
      renderCardsReorderBoard,
    };
  }

  const p = params as UseCardLayoutReorderDualParams<T>;
  return {
    slotMode: 'dual',
    cardColumns,
    quadSlots: undefined,
    persistCardColumns,
    cardsReorderAnchorEl,
    closeCardsReorderPopover,
    renderCardsReorderBoard,
    stackDirection: p.stackDirection ?? 'horizontal',
  };
}
